import _ from "lodash"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"

import Client from "data/_client"
import { ApiResult } from "data/interface/api"
import notificationService from "data/notification"
import validationService from "data/validate"
import { diff_obj } from 'data/lib/diff'
import { push_undo } from 'data/lib/undo'
import { handleReferences } from 'data/lib/handle-references'
import incrementUnique from 'data/lib/increment-unique'
import { alphanum } from "components/table/lib/sort"
import { PromiseWatch } from "./lib/promise-helpers"
import { local } from "./lib/local-json"

import chartService, { Chart } from "data/chart"
import propertyService from "data/property"


// private module logic //
export type Asset = {
  assetid: number
  address?: string
  city?: string
  state?: string
  postalcode?: string
}
type AssetMap = {
  [assetid: number]: Asset
}
type AssetStreamState = ApiResult<Asset[]>

const id: string = "asset"
const orgId = _.get(local("auth.profile"), 'organizationId');
const initialState: AssetStreamState  = { id, state: "init", value: [] }
const assetStream = new BehaviorSubject<AssetStreamState>(initialState)
const assetListEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})

let assetListMap: AssetMap = {}
const _fetches_ = PromiseWatch()
const _patches_ = PromiseWatch()

function clear(): void {
  assetStream.next({ id, state: 'ok', value: [] })
  assetListMap = {}
}

function get(assetid: number): object {
  return assetListMap[assetid]
}

function refresh(): Promise<any> {
  return fetchAssets()
}

function fetchAssets(
  limit: number = 0,
  offset: number = 0
): Promise<object[] | { message: string }> {

  clear()
  assetStream.next({ ...assetStream.getValue(), state: 'busy' })

  return _fetches_.watch('assets', Client.get('asset')
  .then((assets: Asset[]) => {
    _.forEach(assets, (asset) => (assetListMap[asset.assetid] = asset))
    // merge results unique by assetid
    assetStream.next({ ...assetStream.getValue(), state: 'ok', value: assets.sort((a, b) => alphanum(_.get(a, "code"), _.get(b, "code"))) })
    return assets
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  }))
}

function fetchOne(assetId: number): Promise<Asset | { message: string }> {
  const currentValue = assetStream.getValue().value;
  assetStream.next({ ...assetStream.getValue(), state: 'busy' })

  return _fetches_.watch('assets', Client.get(`asset?assetid=eq.${assetId}`)
    .then((assets: Asset[]) => {
      _.forEach(assets, (asset) => (assetListMap[asset.assetid] = asset))
      // merge results unique by assetid
      assetStream.next({ ...assetStream.getValue(), state: 'ok', value: _.unionBy(assets, currentValue, 'assetid')})
      validationService.fetch('asset', assetId)
      return assets[0]
    })
    .catch((err: { message: string }) => {
      assetStream.next({ ...assetStream.getValue(), state: 'error', error: err })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      return err
    })
  )
}

async function create(code:string, name:string, propertytype:string): Promise<Asset | { message: string }> {
  const currentValue = assetStream.getValue().value
  assetStream.next({ ...assetStream.getValue(), state: 'busy' })

  // assign the current default chart to the new asset or create and assign if a default chart doesn't currently exist
  const charts = await chartService.fetchCharts()
  let defaultChart = _(charts).filter((c) => c.isdefault === true).first()
  if(!defaultChart) {
    defaultChart = await chartService.create({
      isdefault: true,
      code: "Default Chart",
      name: "Default Chart"
    } as Chart)
  }

  const payload = {
    organizationid: orgId,
    code,
    name,
    propertytype,
    active: true,
    country: "US",
    currency: "USD",
    areametric: "sqft",
    chartid: defaultChart.chartid
  }

  return Client.post(`asset`, payload)
    .then((assetList: Asset) => {
      push_undo({
        undofunc: () => deleteOne(assetList['assetid']),
        redofunc: () => create(code, name, propertytype)
      })
      assetListMap[assetList['assetid']] = assetList
      // merge results unique by assetid
      const currentValue = assetStream.getValue().value
      assetStream.next({
        ...assetStream.getValue(),
        state: 'ok',
        value: _.unionBy([assetList], currentValue, 'assetid')
      })
      assetListEventStream.next({ type: 'ASSET_LIST_CREATED', payload: assetList })
      validationService.fetchAll('asset')
      return assetList
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      assetStream.next({ ...assetStream.getValue(), state: 'error', error: err })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      fetchAssets()
      return err
    })
}

function copy(assetid:number, code:string) {
  const currentValue = assetStream.getValue().value;
  assetStream.next({ ...assetStream.getValue(), state: 'busy' });

  const payload = {table: 'asset', recordid: assetid, _synchronous: true, code: incrementUnique(code, "code", currentValue)};

  Client.post(`msg/svc_recordcopy`, payload)
    .then((assets: Asset) => {
      const newRecord = assets[0]
      push_undo({
        undofunc: () => deleteOne(newRecord['assetid']),
        redofunc: () => copy(assetid, code)
      });
      assetListMap[newRecord['assetid']] = newRecord
      // merge results unique by assetid
      const currentValue = assetStream.getValue().value
      assetStream.next({
        ...assetStream.getValue(),
        state: 'ok',
        value: _.unionBy([newRecord], currentValue, 'assetid').sort((a, b) => alphanum(a.code, b.code))
      })
      assetListEventStream.next({ type: 'ASSET_LIST_COPIED', payload: newRecord })
      validationService.fetchAll('asset')
      return newRecord
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      assetStream.next({ ...assetStream.getValue(), state: 'error', error: err })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchAssets()
      return err
    })
}

function copies(assetid:number, count: number) {
  const currentValue = assetStream.getValue().value;
  assetStream.next({ ...assetStream.getValue(), state: 'busy' });

  const payload = {table: 'asset', recordid: assetid, _synchronous: true, unique: true, count};

  Client.post(`msg/svc_recordcopy`, payload)
    .then((assets: Asset) => {
      push_undo({
        undofunc: () => _.forEach(assets, asset => deleteOne(asset.assetid)),
        redofunc: () => copies(assetid, count)
      });
      _.forEach(assets, newRecord => assetListMap[newRecord['assetid']] = newRecord)
      // merge results unique by assetid
      const currentValue = assetStream.getValue().value
      assetStream.next({
        ...assetStream.getValue(),
        state: 'ok',
        value: _.unionBy(assets, currentValue, 'assetid').sort((a, b) => alphanum(a.code, b.code))
      })
      assetListEventStream.next({ type: 'ASSET_LIST_COPIED', payload: assets })
      validationService.fetchAll('asset')
      return assets
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      assetStream.next({ ...assetStream.getValue(), state: 'error', error: err })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchAssets()
      return err
    })
}

async function deleteOne(
  assetid: number
): Promise<boolean | { message: string }> {
  const currentValue = assetStream.getValue().value

  const deleteFn = () => deleteOne(assetid)
  const refreshFn = () => fetchAssets()
  if (!(await handleReferences('asset', assetid, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted assetid
  const value = _(currentValue)
    .filter((p) => p.assetid !== assetid)
    .value()
  value.forEach((p) => (assetListMap[p.assetid] = p))

  assetStream.next({ ...assetStream.getValue(), state: 'ok', value })

  // update the db
  return _patches_.all(assetid).then(() =>
  Client.del(`asset?assetid=eq.${assetid}`)
    .then((assetList: Asset) => {
      delete assetListMap[assetList['assetid']]
      push_undo({
        undofunc: () => {
          Client.post('asset', assetList)
          assetListMap[assetList['assetid']] = assetList
          assetStream.next({ ...assetStream.getValue(), state: 'ok', value: currentValue })
        },
        redofunc: () => deleteOne(assetid)
      })
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      assetStream.next({ ...assetStream.getValue(), state: 'error', error: err, value: currentValue })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchAssets()
      return err
    })
  )
}

function patch(assetList: {
  assetid: number
}): Promise<Asset | { message: string }> {
  const currentValue = assetStream.getValue().value
  const assetid: number = assetList.assetid
  const currentassets = _.find(currentValue, { assetid })
  const changeset = diff_obj(assetList, currentassets)
  if (_.isEmpty(changeset)) return new Promise(() => currentValue)
  push_undo({
    undofunc: () => patch(currentassets),
    redofunc: () => patch(assetList)
  })

  // optimistic update
  assetStream.next({
    ...assetStream.getValue(),
    state: 'ok',
    value: _.set(_.cloneDeep(currentValue), _.findIndex(currentValue, {assetid}), assetList)
  })

  return _patches_.watch(assetid, Client.patch(`asset?assetid=eq.${assetid}`, changeset)
    .then((mf: Asset[]) => {
      _.forEach(mf, (assetList) => (assetListMap[assetList.assetid] = assetList))
      validationService.fetchAll('asset')
      return mf[0]
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      assetStream.next({ ...assetStream.getValue(), state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      fetchAssets()
      return err
    }))
}


// public module interface //

const assetService = {
  stream: () => assetStream.asObservable(),
  value: () => assetStream.getValue().value,
  eventStream: () => assetListEventStream.asObservable(),
  clear,
  copy,
  copies,
  create,
  deleteOne,
  get,
  fetchAssets,
  fetchOne,
  patch,
  refresh
}

export default assetService

